import { FC, HTMLAttributes, PropsWithChildren, useCallback, useState } from "react"
import { Box, BoxProps, SortDirection, Stack, StackProps, styled, alpha } from "@mui/material"
import { classes } from "@tm/utils"
import { LEFT_ROW_INDICATOR, ZINDEX_BASE } from "./configs"
import { CellContentPosition, TableVariants } from "./interfaces"
import { Icon } from "../../muiStyled/DataDisplay/Icons"

type TableColumnHeaderProps = {
    variant: TableVariants
    minWidth?: number | string
    fontWeight?: "bold" | "default"
    alignContent?: CellContentPosition
    backgroundColor?: "default" | "paper" | "none" | "inherit" | string
    textColor?: string
    sx?: BoxProps["sx"]
}

type TableColumnSortProps = {
    sortOrder?: SortDirection
}

type TableColumnHeaderType = FC<TableColumnHeaderProps & TableColumnSortProps & HTMLAttributes<HTMLSpanElement>>

function getParentColor(el: HTMLElement) {
    const probe = document.createElement("div")
    document.body.appendChild(probe)
    const transparentValue = window.getComputedStyle(probe).backgroundColor
    document.body.removeChild(probe)

    function iterate(iterationEl: HTMLElement): string {
        const parent = iterationEl.parentElement
        if (parent && window.getComputedStyle(parent).backgroundColor !== transparentValue) {
            return alpha(window.getComputedStyle(parent).backgroundColor, 1)
        }
        if (!parent) {
            return transparentValue
        }
        return iterate(parent)
    }

    return iterate(el)
}

function shouldForwardProp(prop: PropertyKey) {
    return !["variant", "minWidth", "fontWeight", "alignContent", "textColor"].includes(prop as string)
}

function TableColumnHeaderComponent(props: TableColumnHeaderProps & TableColumnSortProps & HTMLAttributes<HTMLDivElement>) {
    const { sortOrder, children, className, backgroundColor, sx, ...rest } = props
    const newClassName = classes("Table-columnHeader", className)
    const [bgColor, setBgColor] = useState(backgroundColor)

    const handleRef = useCallback(
        (ref: HTMLDivElement) => {
            if (backgroundColor === "inherit" && ref) {
                const color = getParentColor(ref)
                setBgColor(color)
            }
        },
        [backgroundColor]
    )

    if (sortOrder !== undefined) {
        return (
            <Box className={newClassName} {...rest} ref={handleRef} sx={{ backgroundColor: bgColor, ...sx }}>
                <TableColumnHeaderSortContent sortOrder={sortOrder}>{children}</TableColumnHeaderSortContent>
            </Box>
        )
    }
    return (
        <Box className={newClassName} {...rest} ref={handleRef} sx={{ backgroundColor: bgColor, ...sx }}>
            {children}
        </Box>
    )
}

export const TableColumnHeader: TableColumnHeaderType = styled(TableColumnHeaderComponent, { shouldForwardProp })<TableColumnHeaderProps>(({
    theme,
    variant,
    minWidth,
    fontWeight,
    alignContent,
    backgroundColor,
    sortOrder,
    textColor,
}) => {
    const elementListOverwrites = theme.overwrites?.components?.elementList ?? {}
    const overwritesBorderLeftWidth = elementListOverwrites?.backgroundColor?.borderLeftWidth
    const borderLeftWidth = overwritesBorderLeftWidth || LEFT_ROW_INDICATOR

    return {
        ...theme.typography.label,
        ...(textColor && {
            color: textColor,
        }),
        textTransform: "uppercase",
        cursor: sortOrder !== undefined ? "pointer" : "inherit",
        zIndex: ZINDEX_BASE + 1,
        opacity: 1,
        alignItems: "center",
        ...(backgroundColor !== "inherit" && {
            backgroundColor:
                backgroundColor === "default" || backgroundColor === undefined
                    ? theme.palette.background.default
                    : backgroundColor === "paper"
                      ? theme.palette.background.paper
                      : backgroundColor,
        }),
        position: "sticky",
        top: 0,
        textAlign: alignContent || "start",
        padding: variant === "small" ? theme.spacing(1, 0.5, 0.5, 0.5) : theme.spacing(1),
        ...(minWidth && {
            minWidth: typeof minWidth === "number" ? `${minWidth}px` : minWidth,
        }),

        ...(fontWeight === "bold" && {
            fontWeight: 800,
        }),

        ...(variant === "small" && {
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            fontSize: "12px",
        }),

        [`&:first-of-type`]: {
            paddingLeft:
                variant === "small" ? `${parseInt(theme.spacing(0.5)) + borderLeftWidth}px` : `${parseInt(theme.spacing(1)) + borderLeftWidth}px`,
            borderLeft: `${borderLeftWidth}px solid transparent`,
        },
    }
})

export function TableColumnHeaderSortContent(props: PropsWithChildren<{ sortOrder?: SortDirection } & StackProps>) {
    const { children, sortOrder, ...stackProps } = props
    return (
        <Stack direction="row" alignItems="center" sx={{ cursor: "pointer" }} {...stackProps}>
            {children}
            <Stack ml={1}>
                <Icon name="up" color={sortOrder === "asc" ? "#000" : "#999"} sx={{ marginBottom: "-1px", fontSize: "10px" }} />
                <Icon name="down" color={sortOrder === "desc" ? "#000" : "#999"} sx={{ marginTop: "-2px", fontSize: "10px" }} />
            </Stack>
        </Stack>
    )
}
